// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

import React, { useState } from "react";
import type { Theme } from "../lib/theme";
import type { FinalTotals } from "../lib/calculations";
import type { SubSchedule } from "../lib/schedule";
import type { Proposal } from "../types";
import type { CostSchedule } from "../lib/costSchedule";
import type { CostLeaf } from "../lib/costSchedule";
import { buildCostLeaves, loadLeaves, entrySpan, resolveEntry, rollupSpan, spreadLeaves } from "../lib/costSchedule";
import * as Icon from "../components/OEIcons";
import type { PhaseGroup } from "../lib/phaseGroups";
import { DEFAULT_PHASE_GROUPS, getSubPhaseGroup } from "../lib/phaseGroups";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

type DurationUnit = "weeks" | "months" | "days";

/** Dark or light text for a given fill. White-on-pastel was unreadable in
 *  light mode; this picks by the fill's relative luminance. */
function readableOn(hex: string): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(hex);
  if (!m) return "#fff";
  const lin = (c: number) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  const L = 0.2126 * lin(parseInt(m[1], 16)) + 0.7152 * lin(parseInt(m[2], 16)) + 0.0722 * lin(parseInt(m[3], 16));
  return L > 0.45 ? "#0f1117" : "#fff";
}

interface PrelimSchedule {
  projectStartDate: string;
  /** Legacy. The project has ONE duration, which lives on Project Info and
   *  is edited from there and from the Schedule toolbar. This field is
   *  written by the default and the old totalWeeks migration and read by
   *  nothing. Wiring it back up re-creates the drift it was removed for:
   *  a 12-month schedule under an 18-month estimate. */
  totalDuration: number;
  durationUnit: DurationUnit;
  /** Legacy. Written by a scheduler that no longer exists; never read. */
  phases?: unknown[];
}

type CfDetail = { monthKey: string; label: string; weekStart: number; weekEnd: number } | null;
type SchedDetail = { phaseId: string; scope: "phase" | "division" | "subcategory"; divId?: string; subId?: string } | null;
type CfView = "phase" | "package";

const toWeeks = (n: number, unit: DurationUnit) =>
  unit === "months" ? Math.round(n * 4.333) : unit === "days" ? Math.round(n / 7) : n;

// Find dominant proposal for a subcategory (by cost)
function dominantProposalId(sub: any, itemState: Record<string, any>): string {
  const costByProp: Record<string, number> = {};
  (sub.rows || []).forEach((row: any) => {
    if (row.type !== "item") return;
    const it = itemState[row.id];
    if (!it) return;
    const prop = it.proposal || "__unassigned__";
    costByProp[prop] = (costByProp[prop] || 0) + (it.qty || 0) * (it.unitCost || 0);
  });
  let best = "__unassigned__", bestCost = -1;
  for (const [pid, cost] of Object.entries(costByProp)) {
    if (cost > bestCost) { best = pid; bestCost = cost; }
  }
  return best;
}

interface CashflowTabProps {
  T: Theme;
  isMobile: boolean;
  isTablet: boolean;
  schedule: PrelimSchedule;
  divisions: any[];
  subTotals: Record<string, number>;
  final: FinalTotals;
  fmt: (n: number) => string;
  setActiveTab: (tab: string) => void;
  cfDetail: CfDetail;
  setCfDetail: React.Dispatch<React.SetStateAction<CfDetail>>;
  setSchedDetail: React.Dispatch<React.SetStateAction<SchedDetail>>;
  costSchedule: CostSchedule;
  /** Project duration in months -- the one Project Info edits. */
  duration: number;
  // The same inputs computeFinal uses, so each line item can be loaded with
  // its own share of markup and contingency rather than smearing one ratio.
  overhead: number;
  profit: number;
  contingency: number;
  markupMode: string;
  divisionMarkups: Record<string, { overhead?: number | null; profit?: number | null }>;
  proposals?: Proposal[];
  itemState?: Record<string, any>;
  phaseGroups?: PhaseGroup[];
}

export default function CashflowTab({
  T, isMobile, isTablet, schedule, divisions, subTotals, final, fmt,
  setActiveTab, cfDetail, setCfDetail, setSchedDetail, costSchedule, duration,
  overhead, profit, contingency, markupMode, divisionMarkups,
  proposals = [], itemState = {},
  phaseGroups = DEFAULT_PHASE_GROUPS,
}: CashflowTabProps) {

  const [cfView, setCfView] = useState<CfView>("phase");
  // On by default: the curve should total to the contract value, which is
  // what an owner or lender reads it for. Off gives the direct-cost curve,
  // which is what you want when planning buyout.
  const [includeMarkup, setIncludeMarkup] = useState(true);

  // The chart sized its bars from a fixed 600px budget, so on a wide panel it
  // stopped two-thirds of the way across with dead space to the right, and on
  // a 24-month project the bars were too narrow to label. Measure the box.
  const chartBoxRef = React.useRef<HTMLDivElement>(null);
  const [chartBoxW, setChartBoxW] = useState(0);
  React.useEffect(() => {
    const el = chartBoxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    setChartBoxW(el.clientWidth);
    const ro = new ResizeObserver(entries => setChartBoxW(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Resolved per line item, not per subcategory. An item scheduled separately
  // has to be spent in its own month; reading only the subcategory's entry
  // would leave that money where the subcategory sits. With no item-level
  // entries the leaves of a subcategory sum to its subtotal, so this is
  // identical to the old per-subcategory spread.
  const leavesBySub = React.useMemo(() => buildCostLeaves(divisions, itemState), [divisions, itemState]);

  // What actually gets spread. Same leaves, same schedules; with the switch
  // on each carries its markup and contingency, so the curve totals to
  // final.total instead of stopping at final.subtotal.
  const spreadLeavesBySub = React.useMemo(() => {
    if (!includeMarkup) return leavesBySub;
    const m = { overhead, profit, contingency, markupMode, divisionMarkups };
    const out: Record<string, CostLeaf[]> = {};
    for (const [subId, leaves] of Object.entries(leavesBySub)) {
      out[subId] = loadLeaves(leaves, itemState, l => l.chain[2], m);
    }
    return out;
  }, [leavesBySub, includeMarkup, itemState, overhead, profit, contingency, markupMode, divisionMarkups]);

  const cfUnit: DurationUnit = "months";
  const cfTw = Math.max(toWeeks(Math.max(1, duration || 12), cfUnit), 1);
  const cfStart = schedule.projectStartDate
    ? new Date(schedule.projectStartDate + "T00:00:00")
    : null;

  const cfWeekToDate = (w: number) => {
    if (!cfStart) return null;
    const d = new Date(cfStart);
    d.setDate(d.getDate() + w * 7);
    return d;
  };

  const cfFmtShort = (d: Date | null) =>
    d ? d.toLocaleDateString("default", { month: "short", day: "numeric" }) : "";

  // Weekly spend. Filled below from the resolved schedule; zero until then.
  const cfWeekly = Array(cfTw).fill(0) as number[];

  // Spread from the resolved schedule
  const hasSubSched = Object.values(costSchedule).some(e => e?.bands?.length);
  if (hasSubSched && cfStart) {
    cfWeekly.fill(0);
    divisions.forEach((div: any) => {
      div.subcategories.forEach((sub: any) => {
        spreadLeaves(spreadLeavesBySub[sub.id] || [], costSchedule, cfStart!, cfTw)
          .forEach((amt, w) => { cfWeekly[w] += amt; });
      });
    });
  }

  // ── Package-based weekly arrays ───────────────────────────────────────
  // pkgWeekly[proposalId] = number[]  (one entry per week)
  const pkgWeekly: Record<string, number[]> = {};
  if (cfView === "package" && hasSubSched && cfStart) {
    proposals.forEach(p => { pkgWeekly[p.id] = Array(cfTw).fill(0); });
    pkgWeekly["__unassigned__"] = Array(cfTw).fill(0);

    divisions.forEach((div: any) => {
      div.subcategories.forEach((sub: any) => {
        const cost = subTotals[sub.id] || 0;
        if (cost === 0) return;
        const entry = resolveEntry([sub.id, div.id], costSchedule);
        if (!entry) return;

        const pid = dominantProposalId(sub, itemState);
        const arr = pkgWeekly[pid] ?? pkgWeekly["__unassigned__"];
        spreadLeaves(spreadLeavesBySub[sub.id] || [], costSchedule, cfStart!, cfTw)
          .forEach((amt, w) => { arr[w] += amt; });
      });
    });
  }

  // ── Phase-group-based weekly arrays (when subSchedule data is available) ──
  // pgWeekly[phaseGroupId] = number[]  (weekly spend)
  const pgWeekly: Record<string, number[]> = {};
  const activePgList: PhaseGroup[] = [];
  if (hasSubSched && cfStart) {
    phaseGroups.forEach(pg => { pgWeekly[pg.id] = Array(cfTw).fill(0); });
    pgWeekly["__other__"] = Array(cfTw).fill(0);
    const activePgSet = new Set<string>();
    divisions.forEach((div: any) => {
      div.subcategories.forEach((sub: any) => {
        // Per subcategory, not per division -- the lookup used to be hoisted
        // out of this loop, which forced every subcategory of a division into
        // one phase's column.
        const pg = getSubPhaseGroup(div.id, sub, phaseGroups);
        const cost = subTotals[sub.id] || 0;
        if (cost === 0) return;
        const entry = resolveEntry([sub.id, div.id], costSchedule);
        if (!entry) return;
        const arr = pg ? pgWeekly[pg.id] : pgWeekly["__other__"];
        if (pg) activePgSet.add(pg.id);
        spreadLeaves(spreadLeavesBySub[sub.id] || [], costSchedule, cfStart!, cfTw)
          .forEach((amt, w) => { arr[w] += amt; });
      });
    });
    phaseGroups.forEach(pg => { if (activePgSet.has(pg.id)) activePgList.push(pg); });
  }

  // Cost with no schedule anywhere up its chain: it cannot appear on the curve,
  // so say so rather than letting the total quietly disagree with the estimate.
  let cfUnscheduled = 0;
  divisions.forEach((div: any) => {
    div.subcategories.forEach((sub: any) => {
      const cost = subTotals[sub.id] || 0;
      if (cost === 0) return;
      const leaves = spreadLeavesBySub[sub.id] || [];
      cfUnscheduled += leaves
        .filter(l => !resolveEntry(l.chain, costSchedule))
        .reduce((a, l) => a + l.cost, 0);
    });
  });

  // ── Monthly bucket builder ────────────────────────────────────────────
  type MBucket = { key: string; label: string; amount: number; weekStart: number; weekEnd: number };
  const cfMonths: MBucket[] = [];
  if (cfStart) {
    const seen = new Map<string, MBucket>();
    cfWeekly.forEach((amt, wi) => {
      const d = cfWeekToDate(wi)!;
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (!seen.has(key)) {
        const b: MBucket = { key, label: d.toLocaleString("default", { month: "short", year: "2-digit" }), amount: 0, weekStart: wi, weekEnd: wi };
        seen.set(key, b); cfMonths.push(b);
      }
      const b = seen.get(key)!; b.amount += amt; b.weekEnd = wi;
    });
  } else {
    for (let w = 0; w < cfTw; w += 4) {
      const end = Math.min(w + 3, cfTw - 1);
      const amt = cfWeekly.slice(w, end + 1).reduce((s, a) => s + a, 0);
      cfMonths.push({ key: `m${w}`, label: `Mo ${Math.floor(w / 4) + 1}`, amount: amt, weekStart: w, weekEnd: end });
    }
  }

  // Per-proposal monthly amounts (for stacked chart)
  type PkgMBucket = MBucket & { pkgAmounts: Record<string, number>; pgAmounts: Record<string, number> };
  const pkgMonths: PkgMBucket[] = cfMonths.map(m => {
    const pkgAmounts: Record<string, number> = {};
    if (cfView === "package" && hasSubSched && cfStart) {
      Object.entries(pkgWeekly).forEach(([pid, weekly]) => {
        const amt = weekly.slice(m.weekStart, m.weekEnd + 1).reduce((s, a) => s + a, 0);
        if (amt > 0) pkgAmounts[pid] = amt;
      });
    }
    const pgAmounts: Record<string, number> = {};
    if (hasSubSched && cfStart) {
      Object.entries(pgWeekly).forEach(([pgId, weekly]) => {
        const amt = weekly.slice(m.weekStart, m.weekEnd + 1).reduce((s, a) => s + a, 0);
        if (amt > 0) pgAmounts[pgId] = amt;
      });
    }
    return { ...m, pkgAmounts, pgAmounts };
  });

  // Active packages (proposals with any spend)
  const activePkgs = cfView === "package"
    ? proposals.filter(p => {
        const weekly = pkgWeekly[p.id];
        return weekly && weekly.some(v => v > 0);
      })
    : [];

  // Package spans (for timeline strip)
  const pkgSpans = cfView === "package" && hasSubSched && cfStart
    ? proposals.map(p => {
        let minW = Infinity, maxW = -1;
        divisions.forEach((div: any) => {
          div.subcategories.forEach((sub: any) => {
            if (dominantProposalId(sub, itemState) !== p.id) return;
            const span = rollupSpan(leavesBySub[sub.id] || [], costSchedule);
            if (!span) return;
            const subS = new Date(span.start + "T00:00:00");
            const subE = new Date(span.end + "T00:00:00");
            const sw = Math.round((subS.getTime() - cfStart!.getTime()) / (7 * 86_400_000));
            const ew = Math.round((subE.getTime() - cfStart!.getTime()) / (7 * 86_400_000));
            if (sw < minW) minW = sw;
            if (ew > maxW) maxW = ew;
          });
        });
        return { proposal: p, startWeek: minW === Infinity ? -1 : minW, endWeek: maxW };
      }).filter(s => s.startWeek >= 0)
    : [];

  const cfMaxMonthly = Math.max(...cfMonths.map((m) => m.amount), 1);
  const cfTotal = cfWeekly.reduce((s, a) => s + a, 0);

  const goToSchedule = () => {
    setActiveTab("schedule");
    window.history.pushState({ oeTab: "schedule" }, "", window.location.hash);
  };

  const padding = isMobile ? "14px 16px" : isTablet ? "20px 24px" : "28px 36px";

  // ── Empty state: nothing scheduled yet ───────────────────────────────
  if (!hasSubSched && cfView === "phase") {
    return (
      <div className="fade-in" style={{ padding: "60px 36px", textAlign: "center", color: T.textWeak }}>
        <div style={{ marginBottom: 16 }}><Icon.BarChart size={32} /></div>
        <div style={{ fontSize: 14, marginBottom: 8, color: T.textSecondary }}>No schedule data yet</div>
        <div style={{ fontSize: 12, marginBottom: 20 }}>Go to the Schedule tab to set subcategory dates, then come back to see phase-group cash flow.</div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={goToSchedule}
            style={{ background: T.borderAccent, border: "none", color: T.textOnGold, fontWeight: 700, padding: "8px 20px", borderRadius: 3, fontSize: 12, fontFamily: FONT, cursor: "pointer" }}>
            Go to Schedule →
          </button>
          <button onClick={() => setCfView("package")}
            style={{ background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, color: T.textSecondary, fontWeight: 600, padding: "8px 20px", borderRadius: 3, fontSize: 12, fontFamily: FONT, cursor: "pointer" }}>
            Switch to Package View
          </button>
        </div>
      </div>
    );
  }

  // ── Package mode empty state (no sub schedule data) ───────────────────
  const pkgEmptyState = cfView === "package" && !hasSubSched ? (
    <div style={{ background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, padding: "32px 24px", marginBottom: 20, textAlign: "center" }}>
      <div style={{ marginBottom: 12 }}><Icon.Clock size={24} /></div>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>No schedule dates set</div>
      <div style={{ fontSize: 11, color: T.textWeak, marginBottom: 16 }}>
        Package view uses subcategory date ranges from the Schedule tab.<br />
        Set start/end dates for your subcategories to see package-based cash flow.
      </div>
      <button onClick={goToSchedule}
        style={{ background: T.borderAccent, border: "none", color: T.textOnGold, fontWeight: 700, padding: "7px 18px", borderRadius: 3, fontSize: 11, fontFamily: FONT, cursor: "pointer" }}>
        Go to Schedule →
      </button>
    </div>
  ) : null;

  return (
    <div className="fade-in" style={{ padding, maxWidth: 1100 }}>

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: T.borderAccent, marginBottom: 4 }}>Cash Flow</div>
          <div style={{ fontSize: 11, color: T.textWeak }}>
            {cfView === "phase"
              ? <>Projected monthly spend from the schedule.{" "}</>
              : <>Monthly spend by trade scope.{" "}</>
            }
            <span style={{ color: T.textSecondary, cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted" }} onClick={goToSchedule}>
              Edit schedule →
            </span>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* By Phase / By Package toggle */}
          <div style={{ display: "flex", background: T.bgInput, borderRadius: 6, border: `1px solid ${T.borderSecondary}`, overflow: "hidden" }}>
            {(["phase", "package"] as CfView[]).map(v => (
              <button key={v} onClick={() => setCfView(v)}
                style={{
                  padding: "5px 12px", border: "none", fontFamily: FONT, fontSize: 10, fontWeight: 700,
                  cursor: "pointer", letterSpacing: ".03em",
                  background: v === cfView ? T.borderAccent : "transparent",
                  color: v === cfView ? "#0a0f1a" : T.textWeak,
                  transition: "background .12s, color .12s",
                }}>
                {v === "phase" ? "By Phase" : "By Package"}
              </button>
            ))}
          </div>

          <label title={includeMarkup
              ? "The curve totals to the project total. Switch off for direct cost only."
              : "Direct cost only. Switch on to add overhead, profit and contingency, spread with the work."}
            style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, color: T.textSecondary, cursor: "pointer", userSelect: "none" }}>
            <input type="checkbox" checked={includeMarkup} onChange={e => setIncludeMarkup(e.target.checked)}
              style={{ accentColor: T.borderAccent, cursor: "pointer" }} />
            Markup &amp; contingency
          </label>

          {cfStart && (
            <div style={{ fontSize: 10, color: T.textWeak, textAlign: "right" }}>
              <div>{cfStart.toLocaleDateString("default", { month: "short", day: "numeric", year: "numeric" })}</div>
              <div style={{ color: T.textSecondary, fontWeight: 600 }}>→ {cfWeekToDate(cfTw)?.toLocaleDateString("default", { month: "short", day: "numeric", year: "numeric" })}</div>
            </div>
          )}
        </div>
      </div>

      {/* ── Package empty state ─────────────────────────────────────── */}
      {pkgEmptyState}

      {/* ── Phase / Package timeline strip ──────────────────────────── */}
      {cfView === "phase" ? (
        <div style={{ background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, padding: "14px 16px", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 600 }}>
              {"Phase Timeline"}
            </div>
            <div style={{ fontSize: 10, color: T.textWeak }}>
              Based on schedule dates
            </div>
          </div>

          {/* Phase timeline */}
          {hasSubSched && cfStart && (
            <div>
              {(() => {
                // Phases overlap in time -- structure starts before site work
                // ends -- so one row can't show them: labels collided and
                // truncated ("Gene", "Demo &", "Foundations & C"). Greedy
                // lanes: each phase takes the first lane free at its start.
                type Span = { pg: PhaseGroup; minW: number; maxW: number; lane: number };
                const spans: Span[] = [];
                activePgList.forEach(pg => {
                  let minW = Infinity, maxW = -1;
                  divisions.forEach((div: any) => {
                    div.subcategories.forEach((sub: any) => {
                      // Tested per subcategory: an overridden one belongs to
                      // this phase even when its division does not.
                      if (getSubPhaseGroup(div.id, sub, phaseGroups)?.id !== pg.id) return;
                      const span = rollupSpan(leavesBySub[sub.id] || [], costSchedule);
                      if (!span) return;
                      const sw = Math.round((new Date(span.start + "T00:00:00").getTime() - cfStart!.getTime()) / (7 * 86_400_000));
                      const ew = Math.round((new Date(span.end + "T00:00:00").getTime() - cfStart!.getTime()) / (7 * 86_400_000));
                      if (sw < minW) minW = sw;
                      if (ew > maxW) maxW = ew;
                    });
                  });
                  if (minW !== Infinity) spans.push({ pg, minW, maxW, lane: 0 });
                });
                spans.sort((a, b) => a.minW - b.minW || b.maxW - a.maxW);
                const laneEnds: number[] = [];
                spans.forEach(sp => {
                  let lane = laneEnds.findIndex(e => e <= sp.minW);
                  if (lane < 0) { lane = laneEnds.length; laneEnds.push(sp.maxW); } else laneEnds[lane] = sp.maxW;
                  sp.lane = lane;
                });
                const LANE_H = 26, GAP = 4, lanes = Math.max(laneEnds.length, 1);
                return (
                  <div style={{ position: "relative", height: lanes * LANE_H + (lanes - 1) * GAP + 4, borderRadius: 3, background: `${T.borderPrimary}22`, overflow: "hidden", marginBottom: 12 }}>
                    {spans.map(({ pg, minW, maxW, lane }) => {
                      const left = (minW / cfTw) * 100;
                      const width = ((maxW - minW) / cfTw) * 100;
                      return (
                        <div key={pg.id} title={pg.name}
                          style={{ position: "absolute", left: `${left}%`, width: `${width}%`, top: 2 + lane * (LANE_H + GAP), height: LANE_H,
                            background: pg.color, borderRadius: 3, overflow: "hidden", display: "flex", alignItems: "center" }}>
                          {width > 6 && (
                            <span style={{ padding: "0 8px", fontSize: 11, fontWeight: 700, color: readableOn(pg.color), whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {pg.name}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              {/* Phase group legend */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                {activePgList.map(pg => {
                  const pgTotal = pgWeekly[pg.id]?.reduce((s, v) => s + v, 0) || 0;
                  return (
                    <div key={pg.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.textTertiary }}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: pg.color, flexShrink: 0 }} />
                      <span>{pg.name}</span>
                      <span style={{ color: T.textWeak }}>({fmt(pgTotal)})</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : hasSubSched && pkgSpans.length > 0 ? (
        /* Package timeline strip */
        <div style={{ background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, padding: "14px 16px", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 600 }}>Package Timeline</div>
            <div style={{ fontSize: 10, color: T.textWeak }}>Derived from subcategory schedule dates</div>
          </div>
          <div style={{ position: "relative", height: 32, borderRadius: 3, background: `${T.borderPrimary}22`, overflow: "hidden", marginBottom: 14 }}>
            {pkgSpans.map(({ proposal: p, startWeek, endWeek }) => {
              const pColor = T.proposalColors[p.color % T.proposalColors.length]?.bar || "#4fd1c5";
              const left = Math.max(0, (startWeek / cfTw) * 100);
              const width = Math.max(1, ((endWeek - startWeek) / cfTw) * 100);
              return (
                <div key={p.id}
                  title={`${p.name} · ${cfFmtShort(cfWeekToDate(startWeek))} – ${cfFmtShort(cfWeekToDate(endWeek))}`}
                  style={{ position: "absolute", left: `${left}%`, width: `${width}%`, top: 2, bottom: 2, background: pColor, borderRadius: 3, opacity: .88, overflow: "hidden" }}>
                  {width > 3 && <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", paddingLeft: 8, paddingRight: 4, fontSize: 10, color: "rgba(255,255,255,.96)", fontWeight: 700, whiteSpace: "nowrap", textShadow: "0 1px 2px rgba(0,0,0,.55)", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {p.name}
                  </span>}
                </div>
              );
            })}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "10px 18px" }}>
            {pkgSpans.map(({ proposal: p, startWeek, endWeek }) => {
              const pColor = T.proposalColors[p.color % T.proposalColors.length]?.bar || "#4fd1c5";
              const pkgTotal = pkgWeekly[p.id]?.reduce((s, a) => s + a, 0) || 0;
              return (
                <div key={p.id} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "2px 0" }}>
                  <div style={{ width: 4, alignSelf: "stretch", borderRadius: 3, background: pColor, flexShrink: 0, minHeight: 34 }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.textPrimary, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{p.name}</div>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginTop: 2 }}>
                      <span style={{ fontSize: 10, color: T.textWeak, fontVariantNumeric: "tabular-nums" }}>
                        {cfFmtShort(cfWeekToDate(startWeek))} – {cfFmtShort(cfWeekToDate(endWeek))}
                      </span>
                      <span style={{ fontSize: 11, color: pColor, fontWeight: 700, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{fmt(pkgTotal)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* ── Summary stats ───────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        {[
          { label: "Total Project Cost", value: fmt(final?.total || 0), sub: "incl. markup & contingency" },
          { label: "Projected Cash Flow", value: fmt(cfTotal), sub: includeMarkup ? "incl. markup & contingency" : "direct cost only" },
          // "Unlinked Cost" was subtotal minus the budget of schedule.phases, and
          // nothing has written phases in a long time -- so it reported the whole
          // estimate as unlinked. What actually matters now is scope with no
          // schedule at all, which is genuinely excluded from the curve.
          { label: cfView === "package" ? "Active Packages" : "Unscheduled Cost",
            value: cfView === "package" ? String(activePkgs.length) : fmt(cfUnscheduled),
            sub: cfView === "package" ? "with schedule data" : (cfUnscheduled > 0.5 ? "not in the curve" : "everything is scheduled") },
          { label: "Duration", value: `${duration || 12} ${cfUnit}`, sub: cfStart ? cfWeekToDate(cfTw)?.toLocaleDateString("default", { month: "short", year: "numeric" }) : undefined },
        ].map((s) => (
          <div key={s.label} style={{ background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, padding: "10px 16px", flex: 1, minWidth: 150 }}>
            <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary }}>{s.value}</div>
            {s.sub && <div style={{ fontSize: 10, color: T.textWeak, marginTop: 2 }}>{s.sub}</div>}
          </div>
        ))}
      </div>

      {cfMonths.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: T.textWeak, fontSize: 13 }}>
          Set a project start date in the{" "}
          <span style={{ color: T.borderAccent, cursor: "pointer" }} onClick={goToSchedule}>Schedule tab</span>{" "}
          to see monthly projections.
        </div>
      ) : (
        <>
          {/* ── Bar chart ─────────────────────────────────────────────── */}
          <div style={{ background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, padding: "16px 16px 10px", marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: T.textTertiary, marginBottom: 14, letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 600 }}>Monthly Cash Flow</div>
            {(() => {
              // Legibility: the chart was 180px tall with 60px bars, month labels
              // computed down to 7px, and y-axis values squeezed into a 36px
              // gutter that "$1,265,722" overflowed. Sizes are on the type scale
              // now, and the data text uses the readable grey, not the weakest.
              const chartH = 240;
              const GX = 64; // left gutter for y-axis values
              const avail = chartBoxW > 0 ? chartBoxW - GX - 8 : 880;
              const barW = Math.max(Math.min(Math.floor(avail / cfMonths.length), 72), 24);
              const chartW = cfMonths.length * barW;
              // Month labels are ~40px wide at 11px. Below that, label every
              // second or third month rather than let them collide.
              const labelEvery = barW >= 48 ? 1 : barW >= 30 ? 2 : 3;
              const cumulative: number[] = [];
              cfMonths.reduce((acc, m, i) => { cumulative[i] = acc + m.amount; return cumulative[i]; }, 0);
              const maxCum = cumulative[cumulative.length - 1] || 1;

              return (
                <div ref={chartBoxRef} style={{ overflowX: "auto" }}>
                  <svg width={chartW + GX + 8} height={chartH + 44} style={{ display: "block" }}>
                    {/* Grid lines */}
                    {[0.25, 0.5, 0.75, 1].map((f) => (
                      <g key={f}>
                        <line x1={GX} y1={chartH * (1 - f)} x2={chartW + GX} y2={chartH * (1 - f)} stroke={T.borderPrimary} strokeWidth={1} opacity={0.6} />
                        <text x={GX - 8} y={chartH * (1 - f) + 4} textAnchor="end" fontSize={11} fill={T.textTertiary} fontFamily={FONT} style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(cfMaxMonthly * f)}</text>
                      </g>
                    ))}

                    {/* Bars */}
                    {cfView === "phase" && hasSubSched && activePgList.length > 0
                      ? /* Stacked phase-group bars (when schedule dates are set) */
                        pkgMonths.map((m, i) => {
                          const x = GX + i * barW;
                          let yOffset = chartH;
                          return (
                            <g key={m.key} style={{ cursor: "pointer" }} onClick={() => setCfDetail({ monthKey: m.key, label: m.label, weekStart: m.weekStart, weekEnd: m.weekEnd })}>
                              <rect x={x + 1} y={0} width={barW - 2} height={chartH} fill="transparent" />
                              <title>{`${m.label}: ${fmt(m.amount)}`}</title>
                              {activePgList.map(pg => {
                                const amt = m.pgAmounts[pg.id] || 0;
                                if (amt === 0) return null;
                                const segH = (amt / cfMaxMonthly) * chartH;
                                const segY = yOffset - segH;
                                yOffset -= segH;
                                return <rect key={pg.id} x={x + 2} y={segY} width={barW - 4} height={Math.max(segH, 0)} fill={pg.color} opacity={0.82} />;
                              })}
                              {(i % labelEvery === 0) && <text x={x + barW / 2} y={chartH + 16} textAnchor="middle" fontSize={11} fill={T.textTertiary} fontFamily={FONT}>{m.label}</text>}
                            </g>
                          );
                        })
                      : cfView === "phase"
                        ? /* Solid bars for phase mode (no subSchedule data) */
                          cfMonths.map((m, i) => {
                            const bh = (m.amount / cfMaxMonthly) * chartH;
                            const x = GX + i * barW;
                            return (
                              <g key={m.key} style={{ cursor: "pointer" }} onClick={() => setCfDetail({ monthKey: m.key, label: m.label, weekStart: m.weekStart, weekEnd: m.weekEnd })}>
                                <rect x={x + 1} y={0} width={barW - 2} height={chartH} fill="transparent" />
                                <title>{`${m.label}: ${fmt(m.amount)} — click to inspect`}</title>
                                <rect x={x + 2} y={chartH - bh} width={barW - 4} height={bh} fill={T.borderAccent} opacity={0.75} rx={1} />
                                {(i % labelEvery === 0) && <text x={x + barW / 2} y={chartH + 16} textAnchor="middle" fontSize={11} fill={T.textTertiary} fontFamily={FONT}>{m.label}</text>}
                                {bh > 16 && <text x={x + barW / 2} y={chartH - bh + 13} textAnchor="middle" fontSize={11} fill="#0f1117" fontFamily={FONT} fontWeight={700}>{fmt(m.amount)}</text>}
                              </g>
                            );
                          })
                        : /* Stacked bars for package mode */
                        pkgMonths.map((m, i) => {
                          const x = GX + i * barW;
                          let yOffset = chartH;
                          return (
                            <g key={m.key} style={{ cursor: "pointer" }} onClick={() => setCfDetail({ monthKey: m.key, label: m.label, weekStart: m.weekStart, weekEnd: m.weekEnd })}>
                              <rect x={x + 1} y={0} width={barW - 2} height={chartH} fill="transparent" />
                              <title>{`${m.label}: ${fmt(m.amount)} — click to inspect`}</title>
                              {/* Stacked proposal segments — in proposal order, bottom up */}
                              {[...proposals].reverse().concat(
                                pkgWeekly["__unassigned__"]?.some(v => v > 0)
                                  ? [{ id: "__unassigned__", name: "Unassigned", color: proposals.length % T.proposalColors.length }]
                                  : []
                              ).map(p => {
                                const amt = m.pkgAmounts[p.id] || 0;
                                if (amt === 0) return null;
                                const segH = (amt / cfMaxMonthly) * chartH;
                                const segY = yOffset - segH;
                                yOffset -= segH;
                                const pColor = T.proposalColors[p.color % T.proposalColors.length]?.bar || "#4fd1c5";
                                return (
                                  <rect key={p.id} x={x + 2} y={segY} width={barW - 4} height={Math.max(segH, 0)} fill={pColor} opacity={0.82} />
                                );
                              })}
                              {(i % labelEvery === 0) && <text x={x + barW / 2} y={chartH + 16} textAnchor="middle" fontSize={11} fill={T.textTertiary} fontFamily={FONT}>{m.label}</text>}
                            </g>
                          );
                        })
                    }

                    {/* S-curve */}
                    {cfMonths.length > 1 && (
                      <polyline points={cumulative.map((c, i) => `${GX + (i + 0.5) * barW},${chartH * (1 - c / maxCum)}`).join(" ")}
                        fill="none" stroke="#f6ad55" strokeWidth={2.5} strokeLinejoin="round" />
                    )}
                    {cumulative.map((c, i) => (
                      <circle key={i} cx={GX + (i + 0.5) * barW} cy={chartH * (1 - c / maxCum)} r={4} fill="#f6ad55" stroke={T.bgPanel} strokeWidth={1.5} />
                    ))}
                  </svg>

                  {/* Legend */}
                  <div style={{ display: "flex", alignItems: "center", gap: 18, marginTop: 10, fontSize: 12, color: T.textTertiary, flexWrap: "wrap" }}>
                    {cfView === "phase" ? (
                      <>
                        {activePgList.length > 0 ? activePgList.map(pg => (
                          <span key={pg.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ display: "inline-block", width: 12, height: 12, background: pg.color, borderRadius: 3 }} />
                            {pg.name}
                          </span>
                        )) : (
                          <span><span style={{ display: "inline-block", width: 12, height: 12, background: T.borderAccent, opacity: .85, borderRadius: 3, marginRight: 6, verticalAlign: "middle" }} />Monthly spend</span>
                        )}
                        <span><span style={{ display: "inline-block", width: 18, height: 3, background: "#f6ad55", marginRight: 6, verticalAlign: "middle", borderRadius: 3 }} />Cumulative (S-curve)</span>
                      </>
                    ) : (
                      <>
                        {activePkgs.map(p => {
                          const pColor = T.proposalColors[p.color % T.proposalColors.length]?.bar || "#4fd1c5";
                          return (
                            <span key={p.id} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              <span style={{ display: "inline-block", width: 12, height: 12, background: pColor, borderRadius: 3 }} />
                              {p.name}
                            </span>
                          );
                        })}
                        <span><span style={{ display: "inline-block", width: 18, height: 3, background: "#f6ad55", marginRight: 6, verticalAlign: "middle", borderRadius: 3 }} />Cumulative</span>
                      </>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* ── Monthly table ──────────────────────────────────────────── */}
          <div style={{ background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: FONT }}>
              <thead>
                <tr style={{ background: `${T.borderPrimary}33` }}>
                  {["Month", "Projected Spend", "Cumulative", "% Complete"].map((h) => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: (h === "Projected Spend" || h === "Cumulative") ? "right" : "left", fontSize: 11, color: T.textTertiary, letterSpacing: ".06em", textTransform: "uppercase", borderBottom: `1px solid ${T.borderSecondary}`, fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pkgMonths.map((m, i) => {
                  const cum = cfMonths.slice(0, i + 1).reduce((s, mm) => s + mm.amount, 0);
                  const pct = cfTotal > 0 ? (cum / cfTotal) * 100 : 0;
                  // Mini package breakdown bar
                  const hasPkgBreakdown = cfView === "package" && Object.keys(m.pkgAmounts).length > 0;
                  return (
                    <tr key={m.key}
                      onClick={() => setCfDetail({ monthKey: m.key, label: m.label, weekStart: m.weekStart, weekEnd: m.weekEnd })}
                      title="Click to see breakdown"
                      style={{ borderBottom: `1px solid ${T.borderPrimary}22`, cursor: "pointer", background: i % 2 === 0 ? "transparent" : T.bgAccentFaint }}>
                      <td style={{ padding: "10px 14px", color: T.textPrimary, fontWeight: 600 }}>
                        {m.label}
                        {hasPkgBreakdown && (
                          <div style={{ display: "flex", gap: 2, marginTop: 3, height: 3, borderRadius: 3, overflow: "hidden", width: 60 }}>
                            {activePkgs.map(p => {
                              const pColor = T.proposalColors[p.color % T.proposalColors.length]?.bar || "#4fd1c5";
                              const share = m.amount > 0 ? (m.pkgAmounts[p.id] || 0) / m.amount : 0;
                              return share > 0 ? <div key={p.id} style={{ flex: share, background: pColor, opacity: .85 }} /> : null;
                            })}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "10px 14px", color: T.textPrimary, fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{fmt(m.amount)}</td>
                      <td style={{ padding: "10px 14px", color: T.textSecondary, fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{fmt(cum)}</td>
                      <td style={{ padding: "10px 14px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ flex: 1, height: 6, background: `${T.borderPrimary}55`, borderRadius: 3, overflow: "hidden" }}>
                            <div style={{ width: `${pct}%`, height: "100%", background: T.borderAccent, borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 12, color: T.textTertiary, width: 40, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{pct.toFixed(0)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: `1px solid ${T.borderSecondary}`, background: `${T.borderPrimary}22` }}>
                  <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 700, color: T.textPrimary }}>Total</td>
                  <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 700, color: T.textPrimary, fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{fmt(cfTotal)}</td>
                  <td colSpan={2} style={{ padding: "10px 14px", fontSize: 11, color: T.textTertiary }}>
                    {(() => {
                      // With everything scheduled and the switch on, this is
                      // zero and says nothing. Any residue is then genuinely
                      // unscheduled cost, not markup, and is named as such.
                      const target = includeMarkup ? (final?.total || 0) : (final?.subtotal || 0);
                      const gap = target - cfTotal;
                      if (Math.abs(gap) < 1) return includeMarkup ? "" : ` (direct cost — ${fmt((final?.total || 0) - (final?.subtotal || 0))} markup & contingency not shown)`;
                      return ` (${fmt(gap)} unscheduled, not in curve)`;
                    })()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}

      {/* ── Monthly detail modal ─────────────────────────────────────── */}
      {cfDetail && (() => {
        const ws = cfDetail.weekStart, we = cfDetail.weekEnd;
        const weeksInMonth = we - ws + 1;

        if (cfView === "package" && hasSubSched) {
          // ── Package detail modal ──────────────────────────────────────
          const monthBucket = pkgMonths.find(m => m.key === cfDetail.monthKey);
          const monthTotal = monthBucket?.amount || 0;

          // For each package, find which subs are active this month and their amounts
          type PkgRow = {
            proposal: Proposal;
            colorHex: string;
            amount: number;
            subRows: Array<{ divCode: string; subName: string; amount: number }>;
          };

          const pkgRows: PkgRow[] = activePkgs.map(p => {
            const pColor = T.proposalColors[p.color % T.proposalColors.length]?.bar || "#4fd1c5";
            const monthWeeklyAmt = (pkgWeekly[p.id] || []).slice(ws, we + 1).reduce((s, a) => s + a, 0);
            if (monthWeeklyAmt < 0.5) return null;

            const subRows: Array<{ divCode: string; subName: string; amount: number }> = [];
            divisions.forEach((div: any) => {
              div.subcategories.forEach((sub: any) => {
                if (dominantProposalId(sub, itemState) !== p.id) return;
                const cost = subTotals[sub.id] || 0;
                if (cost === 0) return;
                if (!cfStart) return;
                const inMonth = spreadLeaves(spreadLeavesBySub[sub.id] || [], costSchedule, cfStart, cfTw)
                  .slice(ws, we + 1).reduce((a, b) => a + b, 0);
                if (inMonth < 0.5) return;
                subRows.push({ divCode: div.code, subName: sub.name, amount: inMonth });
              });
            });
            subRows.sort((a, b) => b.amount - a.amount);

            return { proposal: p, colorHex: pColor, amount: monthWeeklyAmt, subRows };
          }).filter(Boolean) as PkgRow[];

          return (
            <>
              <div onClick={() => setCfDetail(null)} style={{ position: "fixed", inset: 0, zIndex: 900, background: "rgba(0,0,0,0.55)" }} />
              <div role="dialog" aria-modal={true}
                style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 901, background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, width: 720, maxWidth: "94vw", maxHeight: "84vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.55)", fontFamily: FONT }}>
                <div style={{ padding: "16px 22px 12px", borderBottom: `1px solid ${T.borderSecondary}`, display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase" }}>Package cash flow — {cfDetail.label}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary, marginTop: 2 }}>
                      {cfDetail.label}
                      {cfStart && <span style={{ fontSize: 11, color: T.textWeak, marginLeft: 8, fontWeight: 400 }}>
                        · {cfFmtShort(cfWeekToDate(ws))} – {cfFmtShort(cfWeekToDate(we + 1))}
                      </span>}
                    </div>
                    <div style={{ fontSize: 10, color: T.textWeak, marginTop: 2 }}>
                      {pkgRows.length} active package{pkgRows.length === 1 ? "" : "s"} · {weeksInMonth} week{weeksInMonth === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".06em", textTransform: "uppercase" }}>Projected spend</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: T.borderAccent, marginTop: 1, fontVariantNumeric: "tabular-nums" }}>{fmt(monthTotal)}</div>
                  </div>
                  <button onClick={() => setCfDetail(null)} title="Close (Esc)"
                    style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textSecondary, cursor: "pointer", fontSize: 14, width: 28, height: 28, borderRadius: 3, fontFamily: FONT, flexShrink: 0, lineHeight: 1, padding: 0 }}>
                    <Icon.X size={10} />
                  </button>
                </div>
                <div style={{ flex: 1, overflowY: "auto", padding: "10px 22px 14px" }}>
                  {pkgRows.length === 0 ? (
                    <div style={{ fontSize: 11, color: T.textWeak, fontStyle: "italic", padding: "30px 0", textAlign: "center" }}>No package spend projected this month.</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {pkgRows.map(r => {
                        const sharePct = monthTotal > 0 ? (r.amount / monthTotal) * 100 : 0;
                        return (
                          <div key={r.proposal.id}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 6, borderBottom: `1px solid ${T.borderSecondary}55`, marginBottom: 6 }}>
                              <div style={{ width: 6, alignSelf: "stretch", minHeight: 28, borderRadius: 3, background: r.colorHex }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: T.textPrimary }}>{r.proposal.name}</div>
                                <div style={{ fontSize: 10, color: T.textWeak, marginTop: 1 }}>{r.subRows.length} scope item{r.subRows.length === 1 ? "" : "s"} active this month</div>
                              </div>
                              <div style={{ textAlign: "right", flexShrink: 0 }}>
                                <div style={{ fontSize: 12, color: r.colorHex, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmt(r.amount)}</div>
                                <div style={{ fontSize: 10, color: T.textWeak, marginTop: 1 }}>{sharePct.toFixed(0)}% of month</div>
                              </div>
                            </div>
                            {r.subRows.length > 0 && (
                              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                                <thead>
                                  <tr style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".05em", textTransform: "uppercase" }}>
                                    <th style={{ textAlign: "left", padding: "3px 6px 3px 0", fontWeight: 600 }}>Scope</th>
                                    <th style={{ textAlign: "right", padding: "3px 0 3px 6px", fontWeight: 600, width: 110 }}>This month</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {r.subRows.map((s, si) => (
                                    <tr key={si}>
                                      <td style={{ padding: "3px 6px 3px 0", color: T.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 420 }}>
                                        <span style={{ color: T.textWeak, marginRight: 6, fontSize: 10, fontVariantNumeric: "tabular-nums" }}>{s.divCode}</span>
                                        {s.subName}
                                      </td>
                                      <td style={{ padding: "3px 0 3px 6px", textAlign: "right", color: T.textPrimary, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmt(s.amount)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div style={{ padding: "10px 22px", borderTop: `1px solid ${T.borderSecondary}`, display: "flex", alignItems: "center", justifyContent: "flex-end", fontSize: 10, color: T.textWeak }}>
                  <span>Press <kbd style={{ background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3, padding: "1px 5px", fontFamily: FONT, fontSize: 10 }}>Esc</kbd> to close</span>
                </div>
              </div>
            </>
          );
        }

        // ── Phase detail modal ──────────────────────────────────
        // Built from the phases and the resolved schedule -- the same source the
        // chart above uses. It used to be built from schedule.phases, which no
        // editor has written since the Schedule tab was rebuilt, so it always
        // reported "0 active phases" and a zero total over a month the chart
        // showed real spending in.
        type PhaseRow = {
          phase: { id: string; name: string; color: string };
          amount: number;
          subRows: Array<{ subId: string; divCode: string; subCode: string; subName: string; amount: number }>;
        };
        const phaseRows: PhaseRow[] = activePgList.map(pg => {
          const amount = (pgWeekly[pg.id] || []).slice(ws, we + 1).reduce((s, a) => s + a, 0);
          if (amount < 0.5) return null;
          const subRows: PhaseRow["subRows"] = [];
          divisions.forEach((div: any) => {
            div.subcategories.forEach((sub: any) => {
              if (getSubPhaseGroup(div.id, sub, phaseGroups)?.id !== pg.id) return;
              const cost = subTotals[sub.id] || 0;
              if (cost === 0 || !cfStart) return;
              const inMonth = spreadLeaves(spreadLeavesBySub[sub.id] || [], costSchedule, cfStart, cfTw)
                .slice(ws, we + 1).reduce((s, a) => s + a, 0);
              if (inMonth < 0.5) return;
              subRows.push({ subId: sub.id, divCode: div.code, subCode: sub.code || "", subName: sub.name, amount: inMonth });
            });
          });
          subRows.sort((a, b) => b.amount - a.amount);
          return { phase: pg, amount, subRows };
        }).filter(Boolean) as PhaseRow[];
        const monthTotal = phaseRows.reduce((s: number, r: any) => s + r.amount, 0);

        return (
          <>
            <div onClick={() => setCfDetail(null)} style={{ position: "fixed", inset: 0, zIndex: 900, background: "rgba(0,0,0,0.55)" }} />
            <div role="dialog" aria-modal={true}
              style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 901, background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, width: 720, maxWidth: "94vw", maxHeight: "84vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.55)", fontFamily: FONT }}>
              <div style={{ padding: "16px 22px 12px", borderBottom: `1px solid ${T.borderSecondary}`, display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase" }}>Monthly cash flow breakdown</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary, marginTop: 2 }}>
                    {cfDetail.label}
                    {cfStart && <span style={{ fontSize: 11, color: T.textWeak, marginLeft: 8, fontWeight: 400 }}>
                      · {cfFmtShort(cfWeekToDate(ws))} – {cfFmtShort(cfWeekToDate(we + 1))}
                    </span>}
                  </div>
                  <div style={{ fontSize: 10, color: T.textWeak, marginTop: 2 }}>
                    {phaseRows.length} active phase{phaseRows.length === 1 ? "" : "s"} · {weeksInMonth} week{weeksInMonth === 1 ? "" : "s"}
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".06em", textTransform: "uppercase" }}>Projected spend</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: T.borderAccent, marginTop: 1, fontVariantNumeric: "tabular-nums" }}>{fmt(monthTotal)}</div>
                </div>
                <button onClick={() => setCfDetail(null)} title="Close (Esc)"
                  style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textSecondary, cursor: "pointer", fontSize: 14, width: 28, height: 28, borderRadius: 3, fontFamily: FONT, flexShrink: 0, lineHeight: 1, padding: 0 }}>
                  <Icon.X size={10} />
                </button>
              </div>
              <div style={{ flex: 1, overflowY: "auto", padding: "10px 22px 14px" }}>
                {phaseRows.length === 0 ? (
                  <div style={{ fontSize: 11, color: T.textWeak, fontStyle: "italic", padding: "30px 0", textAlign: "center" }}>Nothing scheduled in this month.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    {phaseRows.map((r: any) => {
                      const ph = r.phase;
                      const sharePct = monthTotal > 0 ? (r.amount / monthTotal) * 100 : 0;
                      return (
                        <div key={ph.id}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 6, borderBottom: `1px solid ${T.borderSecondary}55`, marginBottom: 6 }}>
                            <div style={{ width: 6, alignSelf: "stretch", minHeight: 28, borderRadius: 3, background: ph.color }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: T.textPrimary, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{ph.name}</div>
                              <div style={{ fontSize: 10, color: T.textWeak, marginTop: 1 }}>
                                {r.subRows.length} scope item{r.subRows.length === 1 ? "" : "s"} active this month
                              </div>
                            </div>
                            <div style={{ textAlign: "right", flexShrink: 0 }}>
                              <div style={{ fontSize: 12, color: ph.color, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmt(r.amount)}</div>
                              <div style={{ fontSize: 10, color: T.textWeak, marginTop: 1 }}>{sharePct.toFixed(0)}% of month</div>
                            </div>
                          </div>
                          {r.subRows.length > 0 && (
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                              <thead>
                                <tr style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".05em", textTransform: "uppercase" }}>
                                  <th style={{ textAlign: "left", padding: "3px 6px 3px 0", fontWeight: 600 }}>Scope</th>
                                  <th style={{ textAlign: "right", padding: "3px 0 3px 6px", fontWeight: 600, width: 110 }}>This month</th>
                                </tr>
                              </thead>
                              <tbody>
                                {r.subRows.map((s: any) => (
                                  <tr key={s.subId}>
                                    <td style={{ padding: "3px 6px 3px 0", color: T.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 320 }}>
                                      <span style={{ color: T.textWeak, marginRight: 6, fontVariantNumeric: "tabular-nums" }}>{s.subCode || s.divCode}</span>
                                      {s.subName}
                                    </td>
                                    <td style={{ padding: "3px 0 3px 6px", textAlign: "right", color: T.textPrimary, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmt(s.amount)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                          {r.subRows.length === 0 && (
                            <div style={{ fontSize: 10, color: T.textWeak, fontStyle: "italic", paddingLeft: 16 }}>
                              No scope scheduled in this month.
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div style={{ padding: "10px 22px", borderTop: `1px solid ${T.borderSecondary}`, display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 10, color: T.textWeak }}>
                <span>{phaseRows.length} phase{phaseRows.length === 1 ? "" : "s"} active</span>
                <span>Press <kbd style={{ background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3, padding: "1px 5px", fontFamily: FONT, fontSize: 10 }}>Esc</kbd> to close</span>
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
}
